import { createContext, createSignal, useContext } from 'solid-js';
import { createStore } from 'solid-js/store';
import { idString, type BrowserOrServerId } from 'Browser/BrowserId';

const PosterRendererContext = createContext();

export function PosterRendererProvider(P)
{
	const [config,setConfig] = createSignal();

	/* The active renderer's capture-and-upload, registered here so that pages can ask for a render
	   at a point of their choosing (eg on publishing) without reaching into the renderer. */
	const [render,setRender] = createSignal<(()=>Promise<void>)|undefined>();

	/* Per-event poster render/upload status, keyed by document id. Lives here, above both the
	   event page (which does the rendering) and the events page (which shows the thumbnails), so
	   the events list can react to an upload started while the user was editing. */
	const [uploads,setUploads] = createStore({});

	const markRendering = (docId:BrowserOrServerId) =>
		setUploads(idString(docId),{status:'rendering'});
	const markRendered = (docId:BrowserOrServerId,poster) =>
		setUploads(idString(docId),{status:'rendered',poster});
	const clearRendering = (docId:BrowserOrServerId) =>
		setUploads(idString(docId),undefined);

	return (
		<PosterRendererContext.Provider value={{config,setConfig,render,setRender,uploads,markRendering,markRendered,clearRendering}}>
			{P.children}
		</PosterRendererContext.Provider>
	);
}

export function usePosterRenderer()
{
	return useContext(PosterRendererContext).config;
}

export function useSetPosterRenderer()
{
	return useContext(PosterRendererContext).setConfig;
}

/* Render the poster now, if there's a renderer mounted and its poster is out of date */
export function useRenderPoster()
{
	const ctx = useContext(PosterRendererContext);
	return async () => await ctx.render()?.();
}

export function useSetRenderPoster()
{
	return useContext(PosterRendererContext).setRender;
}

export function usePosterUploads()
{
	return useContext(PosterRendererContext).uploads;
}

export function usePosterUploadTracker()
{
	const ctx = useContext(PosterRendererContext);
	return {markRendering:ctx.markRendering, markRendered:ctx.markRendered, clearRendering:ctx.clearRendering};
}
